import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { DrawerModal } from './DrawerModal';
import { usePublic } from '../../../context/PublicContext';
import { useAuth } from '../../../context/AuthContext';
import { getCartItems, removeFromCart, clearCart, updateCartQuantity } from '../../../utils/storage';
import { Trash2, ShoppingBag, Minus, Plus, Trash, Package, User, Phone, MapPin } from 'lucide-react';
import { env } from '../../../config/env';
import { Product } from '../../../api/interface/products.interface';
import { motion, AnimatePresence } from 'framer-motion';
import { orderService } from '../../../api/service/order.service';
import { showToast } from '../notifications/showToast';

interface CartModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const getImageUrl = (path: string | null | undefined): string => {
  if (!path) return '';
  if (path.startsWith('http')) return path;
  return `${env.IMAGE_BASE_URL}${path.replace(/^\//, '')}`;
};

const formatPrice = (price: number) => {
  return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'XOF' }).format(price);
};

const formatPhoneNumber = (phone: string): string | null => {
  const cleaned = phone.replace(/[\s\-\(\)\.]/g, '');
  let beninMatch = cleaned.match(/^(?:\+229|00229|0)(\d{8,9})$/);
  if (beninMatch) {
    let digits = beninMatch[1];
    if (digits.startsWith('0')) {
      digits = digits.slice(1);
    }
    if (/^\d{9}$/.test(digits)) {
      return `+229${digits}`;
    }
    return null;
  }
  if (cleaned.startsWith('+')) {
    const match = cleaned.match(/^\+\d{1,3}\d{6,12}$/);
    if (match) return cleaned;
    return null;
  }
  if (cleaned.startsWith('00')) {
    const without00 = cleaned.slice(2);
    const match = without00.match(/^\d{1,3}\d{6,12}$/);
    if (match) return '+' + without00;
    return null;
  }
  return null;
};

export const CartModal: React.FC<CartModalProps> = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const { products, isLoadingProducts } = usePublic();
  const { user } = useAuth();

  const [cartItems, setCartItems] = useState(getCartItems());
  const [totalPrice, setTotalPrice] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: '',
    phone: '',
    address: '',
  });

  // ---- Fonction pour obtenir le prix final (promo ou normal) ----
  const getProductDisplayPrice = (product: Product): number => {
    return product.discount_price ?? product.price;
  };

  useEffect(() => {
    if (user) {
      const fullName = [user.first_name, user.last_name].filter(Boolean).join(' ');
      setForm((prev) => ({
        ...prev,
        name: fullName || prev.name,
        phone: user.phone_number || prev.phone,
      }));
    }
  }, [user]);

  const refreshCart = () => {
    const items = getCartItems();
    setCartItems(items);
  };

  useEffect(() => {
    refreshCart();
    const handleUpdate = () => refreshCart();
    window.addEventListener('cartUpdated', handleUpdate);
    return () => window.removeEventListener('cartUpdated', handleUpdate);
  }, []);

  // ---- Calcul du total en tenant compte des promos ----
  useEffect(() => {
    if (!products || products.length === 0) return;
    let total = 0;
    cartItems.forEach(cartItem => {
      const product = products.find(p => p.id === cartItem.productId);
      if (product) {
        const price = getProductDisplayPrice(product);
        total += price * cartItem.quantity;
      }
    });
    setTotalPrice(total);
  }, [cartItems, products]);

  const handleUpdateQuantity = (productId: string, newQuantity: number) => {
    if (newQuantity <= 0) {
      removeFromCart(productId);
    } else {
      updateCartQuantity(productId, newQuantity);
    }
    refreshCart();
  };

  const handleRemoveItem = (productId: string) => {
    removeFromCart(productId);
    refreshCart();
  };

  const handleClearCart = () => {
    clearCart();
    refreshCart();
    showToast({
      title: 'Panier vidé',
      description: 'Tous les articles ont été supprimés.',
      type: 'info',
      duration: 3000,
    });
  };

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleProceedToCheckout = async () => {
    if (!form.name.trim() || !form.phone.trim() || !form.address.trim()) {
      showToast({
        title: 'Champs manquants',
        description: 'Veuillez remplir votre nom, votre téléphone et votre adresse de livraison.',
        type: 'warning',
        duration: 5000,
      });
      return;
    }

    const formattedPhone = formatPhoneNumber(form.phone.trim());
    if (!formattedPhone) {
      showToast({
        title: 'Numéro de téléphone invalide',
        description: 'Veuillez saisir un numéro international valide (ex: +2290157525606, 002290157525606).',
        type: 'error',
        duration: 6000,
      });
      return;
    }

    if (cartProductList.length === 0) {
      showToast({
        title: 'Panier vide',
        description: 'Ajoutez des produits avant de passer commande.',
        type: 'warning',
        duration: 4000,
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const items = cartProductList.map(({ product, quantity }) => ({
        product_id: product.id,
        quantity,
      }));

      const orderPayload = {
        items,
        customer_name: form.name.trim(),
        customer_phone: formattedPhone,
        customer_address: form.address.trim(),
      };

      const response = await orderService.createOrder(orderPayload);
      const order = response.data;

      showToast({
        title: 'Commande créée',
        description: 'Votre commande a été enregistrée, vous allez être redirigé.',
        type: 'success',
        duration: 3000,
      });

      onClose();
      navigate('/checkout', { replace: true });
    } catch (error: any) {
      console.error('Erreur création commande:', error);
      const message = error?.response?.data?.message || 'Une erreur est survenue. Veuillez réessayer.';
      showToast({
        title: 'Erreur',
        description: message,
        type: 'error',
        duration: 6000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const cartProductList = cartItems
    .map(cartItem => {
      const product = products?.find(p => p.id === cartItem.productId);
      return product ? { ...cartItem, product } : null;
    })
    .filter((item): item is { product: Product; productId: string; quantity: number; affiliateCode?: string | null } => item !== null);

  if (isLoadingProducts) {
    return (
      <DrawerModal isOpen={isOpen} onClose={onClose} title="Mon panier">
        <div className="flex justify-center py-12">
          <div className="h-8 w-8 animate-spin rounded-full border-3 border-primary border-t-transparent" />
        </div>
      </DrawerModal>
    );
  }

  return (
    <DrawerModal isOpen={isOpen} onClose={onClose} title="Mon panier">
      {cartProductList.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <img
            src="/assets/images/illustra/404.svg"
            alt="Panier vide"
            className="w-60 h-60 object-contain mb-6 opacity-80"
          />
          <h3 className="text-xl font-semibold text-gray-700 mb-2">Votre panier est vide</h3>
          <p className="text-gray-500 text-sm mb-8 max-w-xs mx-auto">
            Découvrez nos produits et ajoutez ceux qui vous plaisent.
          </p>
          <button
            onClick={() => {
              onClose();
              navigate('/boutique');
            }}
            className="inline-flex items-center gap-2 px-6 py-2 rounded-full bg-gradient-to-r from-primary to-secondary text-black font-medium hover:scale-105 transition"
          >
            <ShoppingBag size={16} />
            Explorer la boutique
          </button>
        </div>
      ) : (
        <>
          <AnimatePresence mode="popLayout">
            <div className="space-y-4">
              {cartProductList.map(({ product, quantity }, index) => (
                <motion.div
                  key={product.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, x: -100 }}
                  transition={{ delay: index * 0.05, duration: 0.3 }}
                  className="flex gap-4 p-3 rounded-xl bg-gray-50 border border-gray-200 shadow-sm hover:shadow-md transition-all duration-200"
                >
                  <div className="relative w-20 h-20 rounded-lg overflow-hidden bg-gray-200 flex-shrink-0">
                    {product.thumbnail ? (
                      <img
                        src={getImageUrl(product.thumbnail)}
                        alt={product.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Package size={32} className="text-gray-400" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1 space-y-1">
                    <h4 className="font-semibold text-gray-800 line-clamp-1">{product.name}</h4>
                    
                    {/* Affichage du prix avec promotion */}
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-bold text-black">
                        {formatPrice(getProductDisplayPrice(product))}
                      </span>
                      {product.discount_price && product.discount_price < product.price && (
                        <span className="text-xs text-gray-400 line-through">
                          {formatPrice(product.price)}
                        </span>
                      )}
                    </div>

                    <div className="flex items-center gap-3 mt-1">
                      <div className="flex items-center gap-1 bg-gray-200 rounded-full p-0.5">
                        <button
                          onClick={() => handleUpdateQuantity(product.id, quantity - 1)}
                          className="p-1 rounded-full bg-white hover:bg-gray-300 text-gray-700 transition"
                        >
                          <Minus size={12} />
                        </button>
                        <span className="text-sm font-medium text-gray-800 w-6 text-center">{quantity}</span>
                        <button
                          onClick={() => handleUpdateQuantity(product.id, quantity + 1)}
                          className="p-1 rounded-full bg-white hover:bg-gray-300 text-gray-700 transition"
                        >
                          <Plus size={12} />
                        </button>
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => handleRemoveItem(product.id)}
                    className="text-red-500 hover:text-red-700 transition self-center bg-red-50 rounded-full px-2 py-2"
                  >
                    <Trash2 size={18} />
                  </button>
                </motion.div>
              ))}
            </div>
          </AnimatePresence>

          <div className="mt-4 p-4 rounded-xl bg-gray-50 border border-gray-200">
            <h4 className="text-gray-700 font-medium text-sm mb-3">Vos coordonnées</h4>
            <div className="space-y-3">
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                <input
                  name="name"
                  placeholder="Nom complet *"
                  value={form.name}
                  onChange={handleFormChange}
                  className="w-full pl-9 pr-3 py-2 rounded-lg bg-white border border-gray-300 text-gray-800 placeholder-gray-400 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
                />
              </div>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                <input
                  name="phone"
                  placeholder="Téléphone * (ex: +2290157525606)"
                  value={form.phone}
                  onChange={handleFormChange}
                  className="w-full pl-9 pr-3 py-2 rounded-lg bg-white border border-gray-300 text-gray-800 placeholder-gray-400 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
                />
              </div>
              <div className="relative">
                <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                <input
                  name="address"
                  placeholder="Adresse de livraison *"
                  value={form.address}
                  onChange={handleFormChange}
                  className="w-full pl-9 pr-3 py-2 rounded-lg bg-white border border-gray-300 text-gray-800 placeholder-gray-400 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
                />
              </div>
            </div>
          </div>

          <div className="mt-4 pt-4 border-t border-gray-200 sticky bottom-0 bg-white/80 backdrop-blur-sm rounded-b-lg p-2">
            <div className="flex justify-between font-semibold text-lg mb-4 text-gray-800">
              <span>Total</span>
              <span className="text-black">{formatPrice(totalPrice)}</span>
            </div>
            <div className="flex gap-3 justify-between">
              <button
                onClick={handleClearCart}
                className="flex-1 flex items-center justify-center gap-2 rounded-full max-w-fit px-3 py-2 bg-red-500 hover:bg-red-600 text-white font-semibold transition"
              >
                <Trash size={18} />
                Vider
              </button>
              <button
                onClick={handleProceedToCheckout}
                disabled={isSubmitting}
                className={`
                  flex-1 flex items-center justify-center gap-2 rounded-full max-w-fit px-3 py-2 
                  bg-gradient-to-r from-primary to-secondary text-black font-semibold shadow-md 
                  hover:scale-105 transition
                  ${isSubmitting ? 'opacity-60 cursor-not-allowed' : ''}
                `}
              >
                {isSubmitting ? (
                  <>
                    <span className="h-4 w-4 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                    Commande en cours...
                  </>
                ) : (
                  'Passer commande'
                )}
              </button>
            </div>
          </div>
        </>
      )}
    </DrawerModal>
  );
};